import { createContext, useCallback, useEffect, useState } from 'react'

import { useGetAccessToken } from '@/hooks/queries/use-get-access-token'
import { useGetCompanies } from '@/hooks/queries/use-get-companies'
import { useCompaniesServiceHook } from '@/hooks/services/use-companies-service'
import { useAuth } from '@/hooks/use-auth'
import { useClearHistoryState } from '@/hooks/use-clear-history-state'
import { UserCompanyRole } from '@/services/get-access-token'
import { getCompanyByUser, setCompanyByUser } from '@/utils/companies-storage'

import Company, { CompanyPermissions } from '../types/company'

function useCompaniesContext() {
  const { clearHistoryState } = useClearHistoryState()

  const [selectedCompany, setSelectedCompany] = useState({} as Company)
  const { user, isAuthenticated, isLoading: isLoadingAuth } = useAuth()

  const canSearchCompanies = isAuthenticated && !isLoadingAuth

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useGetCompanies({
    enabled: canSearchCompanies,
    onSuccess: (data) => {
      const oldCompany = getCompanyByUser(user.id)

      const company = data.find(
        (newCompany) => newCompany.id === oldCompany?.id,
      )

      handleSelectCompany(company ?? data[0])
    },
  })

  const {
    data: accessTokenResponse,
    isError: isAccessTokenError,
    isLoading: isAccessTokenLoading,
    refetch,
  } = useGetAccessToken('company', selectedCompany?.id, user.id)

  const handleSelectCompany = useCallback(
    (company: Company) => {
      setSelectedCompany(company)
      setCompanyByUser(user.id, company)
    },
    [user],
  )

  const selectCompanyById = useCallback(
    async (id: number) => {
      const company = companies!.find((company) => company.id === id)!

      handleSelectCompany(company)
      clearHistoryState()
    },
    [companies, clearHistoryState, handleSelectCompany],
  )

  useCompaniesServiceHook({
    accessToken: accessTokenResponse?.accessToken,
    refetchAccessToken: async () => {
      const { data } = await refetch({
        throwOnError: true,
        exact: true,
        stale: false,
      })

      return data?.accessToken
    },
  })

  useEffect(() => {
    if (!canSearchCompanies) {
      setSelectedCompany({} as Company)
    }
  }, [canSearchCompanies])

  return {
    companies: companies || [],
    selectedCompany,
    accessToken: accessTokenResponse?.accessToken || '',
    permissions: (accessTokenResponse?.permissions || {}) as CompanyPermissions,
    role: accessTokenResponse?.role || ('' as UserCompanyRole),
    isCompaniesLoading: isCompaniesLoading || isAccessTokenLoading,
    isCompaniesError: isCompaniesError || isAccessTokenError,
    selectCompanyById,
    refetchAccessToken: refetch,
  }
}

export type CompaniesContextData = ReturnType<typeof useCompaniesContext>

export const CompaniesContext = createContext<CompaniesContextData>(
  {} as CompaniesContextData,
)

export function CompaniesProvider({ children }: { children: React.ReactNode }) {
  return (
    <CompaniesContext.Provider value={useCompaniesContext()}>
      {children}
    </CompaniesContext.Provider>
  )
}
